import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import clsx from 'classnames';
import List from '@material-ui/core/List';
import ListSubheader from '@material-ui/core/ListSubheader';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import { GridLeftPanelProps, GridParams } from './register-types';
import { useOriginatorClasses } from './register-api';
import { LocalSearchInput } from 'components/grid/v2/lists/local-search-field';
import { useOriginatorContext } from './register-context';
import { useDebounce } from 'hooks/debounce-value-hook';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: theme.palette.secondary.main,
      boxShadow: theme.shadows[1],
      height: 'calc(100vh - 80px)',
      position: 'sticky',
      top: 80,
      overflow: 'auto',
      padding: 0,
    },
    title: {
      color: 'white',
      fontWeight: 'bold',
      fontSize: 18,
      height: 60,
      lineHeight: '60px',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      zIndex: 1000,
    },
    subTitle: {
      color: 'white',
      fontWeight: 'bold',
      fontSize: 16,
      height: 50,
      lineHeight: '50px',
      paddingLeft: 24,
      zIndex: 1000,
    },
    originatorClasses: {
      marginBottom: 16,
      position: 'sticky',
      top: 60,
      backgroundColor: theme.palette.secondary.main,
      zIndex: 1000,
    },
    divider: {
      border: 'solid',
      padding: '0 16px',
      borderWidth: '1px',
      color: theme.palette.primary.dark,
    },
    letters: {},
    buttonWrapper: {
      width: '100%',
      padding: '0 32px',
      textTransform: 'none',
    },
    buttonText: {
      width: '100%',
      color: 'white',
      fontWeight: 600,
      fontSize: 14,
    },
    activeText: {
      color: theme.palette.primary.main,
    },
  })
);

export const GridLeftPanelV2: React.FC<GridLeftPanelProps> = ({
  letters,
  detailView,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const originatorClasses = useOriginatorClasses();
  const { originatorClass, search, rowsPerPage } = useParams<GridParams>();

  const { setNameFilter, aggregations, nameFilter } = useOriginatorContext();

  const [nameFilterString, setNameFilterString] = useState(
    search ?? nameFilter ?? ''
  );

  const debouncedFilter = useDebounce(nameFilterString, 300);
  //const [nameFilter, setFilter] = useState('');

  useEffect(() => {
    setNameFilter?.(debouncedFilter);
    if (debouncedFilter) {
      history.push(
        `/rejstriky/${originatorClass}/${encodeURIComponent(
          debouncedFilter
        )}/0/${rowsPerPage}`
      );
    }
  }, [setNameFilter, debouncedFilter, history, originatorClass, rowsPerPage]);

  const activeLetter = !!search
    ? decodeURIComponent(search).startsWith('Ch')
      ? 'Ch'
      : decodeURIComponent(search).charAt(0)
    : undefined;

  return (
    <List
      className={classes.root}
      component="nav"
      dense={true}
      subheader={
        <ListSubheader
          component="div"
          className={classes.title}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          Rejstříky
          {!detailView && (
            <LocalSearchInput
              filter={nameFilterString ?? ''}
              setFilter={(s) => setNameFilterString?.(s)}
            />
          )}
        </ListSubheader>
      }
    >
      <div className={classes.originatorClasses}>
        <div className={classes.subTitle}>Třída</div>
        {originatorClasses.map(({ id, label }) => (
          <Button
            key={id}
            className={classes.buttonWrapper}
            component="span"
            variant="text"
            color="default"
            onClick={() => history.push(`/rejstriky/${id.toLocaleLowerCase()}`)}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              component="span"
              variant="body2"
              className={clsx({
                [classes.buttonText]: true,
                [classes.activeText]:
                  id.toLocaleLowerCase() === originatorClass,
              })}
            >
              {label}{' '}
            </Typography>
            <Typography
              component="span"
              variant="body2"
              style={{ color: 'white' }}
            >
              {id !== 'ALL' ? (
                <>{aggregations?.find((a) => a?.key === id)?.value ?? 0}</>
              ) : (
                ''
              )}
            </Typography>
          </Button>
        ))}
        <Divider className={classes.divider} variant="middle" />
      </div>
      {!detailView && (
        <div className={classes.letters}>
          {letters
            .filter((l) =>
              nameFilter === ''
                ? true
                : l.toUpperCase() === nameFilter?.[0]?.toUpperCase()
            )
            .map((letter) => (
              <Button
                key={letter}
                className={classes.buttonWrapper}
                component="span"
                variant="text"
                color="default"
                onClick={() =>
                  history.push(
                    `/rejstriky/${originatorClass}/${encodeURIComponent(
                      letter
                    )}/0/${rowsPerPage}`
                  )
                }
              >
                <Typography
                  component="span"
                  variant="body2"
                  className={clsx({
                    [classes.buttonText]: true,
                    [classes.activeText]: letter === activeLetter,
                  })}
                >
                  {letter}
                </Typography>
              </Button>
            ))}
        </div>
      )}
    </List>
  );
};
